import { useCallback, useState } from 'react';
import {
  MIN_LOCATION_SEARCH_CHARS,
  searchGeographies,
} from '~/features/provider-hlx/service-listings/geographyApi';

export type LocationOption = {
  value: string;
  label: string;
  latitude: number;
  longitude: number;
};

type LocationLookupStatus = 'short' | 'no-match' | 'error';

const LOCATION_MESSAGES: Record<LocationLookupStatus, string> = {
  short: `Type at least ${MIN_LOCATION_SEARCH_CHARS} characters of a city or ZIP code`,
  'no-match': 'No matching city or ZIP',
  error: "Couldn't look up locations. Try again.",
};

/** Looks a city/ZIP up for the "City, ST or ZIP" picker; the coordinates travel with the option. */
export const useLocationOptions = () => {
  const [status, setStatus] = useState<LocationLookupStatus>('short');
  const loadOptions = useCallback(async (input: string): Promise<LocationOption[]> => {
    if (input.trim().length < MIN_LOCATION_SEARCH_CHARS) {
      setStatus('short');
      return [];
    }
    try {
      const options = (await searchGeographies(input)).map((geography) => ({
        value: String(geography.id),
        label: geography.label,
        latitude: Number(geography.latitude),
        longitude: Number(geography.longitude),
      }));
      setStatus(options.length ? 'short' : 'no-match');
      return options;
    } catch {
      setStatus('error');
      return [];
    }
  }, []);
  return { loadOptions, noOptionsMessage: LOCATION_MESSAGES[status] };
};
